'use client';

import { FEATURE_FLAG_KEYS } from 'shop-ui/constants';
import { combineComponent } from 'shop-ui/tools/combineComponent';
import { GRID_LAYOUT_TYPE } from 'shop-ui/section/Grid/rules';
import { OFFICIAL_FONT_SIZE_KEY, createRichText } from 'shop-ui/component/RichText';
import { createMedia, OFFICIAL_MEDIA_KEY } from 'shop-ui/component/Media';
import { type LayoutNodeStyleMap } from 'shop-ui/types';
import i18n from '@shared/i18n';

export enum OFFICIAL_SECTION_IMG_WALL_KEY {
    TWO = 'img-wall-two',
    THREE = 'img-wall-three',
    FOUR = 'img-wall-four',
    MULTIPLE = 'img-wall-multiple',
}

export const officialSectionImgWallList = [
    {
        name: OFFICIAL_SECTION_IMG_WALL_KEY.TWO,
        content: i18n.gettext('Lorem ipsum dolor sit'),
        version: 1,
    },
    {
        name: OFFICIAL_SECTION_IMG_WALL_KEY.THREE,
        content: i18n.gettext(
            'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint.'
        ),
        version: 1,
    },
    {
        name: OFFICIAL_SECTION_IMG_WALL_KEY.FOUR,
        content: i18n.gettext('Lorem ipsum dolor sit'),
        version: 1,
    },
    {
        name: OFFICIAL_SECTION_IMG_WALL_KEY.MULTIPLE,
        content: i18n.gettext('Lorem ipsum dolor sit'),
        version: 1,
    },
];

export function createImgWallTwo(
    content: string = officialSectionImgWallList[0].content
) {
    const heading = createRichText(OFFICIAL_FONT_SIZE_KEY.HEADING_SMALL, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(32, 32, 38, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        [FEATURE_FLAG_KEYS.PADDING_LEFT]: '14%',
        [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '14%',
    });

    const paragraph = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL,
        i18n.gettext(
            'Problems trying to resolve the conflict between the two major realms of Classical physics: Newtonian mechanics'
        ),
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(102, 102, 106, 1)',
            [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
            [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '400',
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '24px',
            [FEATURE_FLAG_KEYS.PADDING_LEFT]: '14%',
            [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '14%',
        }
    );

    const img1 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.PADDING_TOP]: '60px',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '16px',
        [FEATURE_FLAG_KEYS.PADDING_LEFT]: '14%',
        [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '14%',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/img-wall-two-1_v1.png',
    });

    const img2 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '16px',
        [FEATURE_FLAG_KEYS.PADDING_LEFT]: '8.3%',
        [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '37.2%',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/img-wall-two-2_v1.png',
    });

    const img3 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '16px',
        [FEATURE_FLAG_KEYS.PADDING_LEFT]: '8.3%',
        [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '37.2%',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/img-wall-two-2_v1.png',
    });

    return combineComponent(
        i18n.gettext('雙圖片牆'),
        [[heading, paragraph, img1], [img2], [img3]],
        {
            primaryGridType: GRID_LAYOUT_TYPE.SPLIT_H_1C_2C,
            secondaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
            primaryGridPresetStyle: {
                [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '100px',
                [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
                [FEATURE_FLAG_KEYS.GRID_GAP]: '46px',
                [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
                [FEATURE_FLAG_KEYS.PADDING_TOP]: '36px',
                [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '36px',
            },
            secondaryGridPresetStyle: [
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'center',
                },
            ],
            shouldWrap: true,
        }
    );
}

export function createImgWallThree(
    content: string = officialSectionImgWallList[1].content,
    presetStyle: {
        img1?: LayoutNodeStyleMap[string];
        img2?: LayoutNodeStyleMap[string];
        img3?: LayoutNodeStyleMap[string];
        primaryGrid?: LayoutNodeStyleMap[string];
        secondaryGrid?: LayoutNodeStyleMap[string][];
    } = {}
) {
    const {
        img1: img1PresetStyle = {},
        img2: img2PresetStyle = {},
        img3: img3PresetStyle = {},
        primaryGrid: primaryGridPresetStyle = {},
        secondaryGrid: secondaryGridPresetStyle = [],
    } = presetStyle;

    const heading = createRichText(OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(57, 57, 62, 1)',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '500',
        [FEATURE_FLAG_KEYS.PADDING_TOP]: '24px',
    });

    const paragraph = createRichText(
        OFFICIAL_FONT_SIZE_KEY.CUSTOM,
        i18n.gettext('Lorem ipsum dolor'),
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '12',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(102, 102, 106, 1)',
            [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
            [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '500',
        }
    );

    const paragraph2 = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL,
        i18n.gettext(
            'Problems trying to resolve the conflict between the two major realms of Classical physics: Newtonian mechanics'
        ),
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(102, 102, 106, 1)',
            [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
            [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '400',
        }
    );

    const img1 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.PADDING_TOP]: '60px',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '16px',
        [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '21%',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/img-wall-three-1_v1.png',
        ...img1PresetStyle,
    });

    const img2 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '16px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/img-wall-three-2_v1.png',
        ...img2PresetStyle,
    });

    const img3 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '16px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/img-wall-three-3_v1.png',
        ...img3PresetStyle,
    });

    return combineComponent(
        i18n.gettext('三圖片牆'),
        [[paragraph, heading, img1], [[img2], [img3]], [paragraph2]],
        {
            primaryGridType: GRID_LAYOUT_TYPE.SPLIT_H_1C_2C,
            secondaryGridType: [
                GRID_LAYOUT_TYPE.SINGLE_CELL,
                GRID_LAYOUT_TYPE.SPLIT_H_2C,
                GRID_LAYOUT_TYPE.SINGLE_CELL,
            ],
            primaryGridPresetStyle: {
                [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '100px',
                [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
                [FEATURE_FLAG_KEYS.GRID_GAP]: '20px',
                [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
                [FEATURE_FLAG_KEYS.PADDING_TOP]: '70px',
                [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '70px',
                [FEATURE_FLAG_KEYS.GRID_ROW_1]: '0.7fr',
                [FEATURE_FLAG_KEYS.GRID_ROW_2]: '0.3fr',
                ...primaryGridPresetStyle,
            },
            secondaryGridPresetStyle: [
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'center',
                    ...(secondaryGridPresetStyle?.[0] || {}),
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'end',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '24px',
                    ...(secondaryGridPresetStyle?.[1] || {}),
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    ...(secondaryGridPresetStyle?.[2] || {}),
                },
            ],
            shouldWrap: true,
        }
    );
}

export function createImgWallFour(
    content: string = officialSectionImgWallList[2].content,
    content2: string = i18n.gettext(
        'Problems trying to resolve the conflict between the two major realms of Classical physics: Newtonian mechanics'
    ),
    presetStyle: {
        img1?: LayoutNodeStyleMap[string];
        img2?: LayoutNodeStyleMap[string];
        img3?: LayoutNodeStyleMap[string];
        img4?: LayoutNodeStyleMap[string];
        primaryGrid?: LayoutNodeStyleMap[string];
        secondaryGrid?: LayoutNodeStyleMap[string][];
    } = {}
) {
    const heading = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'center',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '23',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(37, 43, 66, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.6',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '1px',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
    });

    const paragraph = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, content2, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'center',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '12',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(115, 115, 115, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '1px',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '400',
        [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '24px',
    });

    const img1 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '16px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/img-wall-four-1_v1.png',
        ...(presetStyle?.img1 && presetStyle?.img1),
    });

    const img2 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '16px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/img-wall-four-2_v1.png',
        ...(presetStyle?.img2 && presetStyle?.img2),
    });

    const img3 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '16px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/img-wall-four-3_v1.png',
        ...(presetStyle?.img3 && presetStyle?.img3),
    });

    const img4 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '16px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/img-wall-four-4_v1.png',
        ...(presetStyle?.img4 && presetStyle?.img4),
    });

    return combineComponent(
        i18n.gettext('四圖片牆'),
        [
            [heading, paragraph],
            [[img1], [img2]],
            [[img3], [img4]],
        ],
        {
            primaryGridType: GRID_LAYOUT_TYPE.SPLIT_V_1C_2C,
            secondaryGridType: [
                GRID_LAYOUT_TYPE.SINGLE_CELL,
                GRID_LAYOUT_TYPE.SPLIT_H_2C,
                GRID_LAYOUT_TYPE.SPLIT_V_2C,
            ],
            primaryGridPresetStyle: {
                [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '100px',
                [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
                [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
                [FEATURE_FLAG_KEYS.GRID_GAP]: '20px',
                [FEATURE_FLAG_KEYS.PADDING_TOP]: '60px',
                [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '60px',
                [FEATURE_FLAG_KEYS.GRID_ROW_1]: '0.3fr',
                [FEATURE_FLAG_KEYS.GRID_ROW_2]: '0.7fr',
                [FEATURE_FLAG_KEYS.GRID_COLUMN_1]: '0.7514fr',
                [FEATURE_FLAG_KEYS.GRID_COLUMN_2]: '0.2486fr',
                ...(presetStyle?.primaryGrid && presetStyle?.primaryGrid),
            },
            secondaryGridPresetStyle: [
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'center',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '10px',
                    ...(presetStyle?.secondaryGrid?.[0] &&
                        presetStyle?.secondaryGrid?.[0]),
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '20px',
                    [FEATURE_FLAG_KEYS.GRID_COLUMN_1]: '1.1017fr',
                    [FEATURE_FLAG_KEYS.GRID_COLUMN_2]: '0.8983fr',
                    ...(presetStyle?.secondaryGrid?.[1] &&
                        presetStyle?.secondaryGrid?.[1]),
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '20px',
                    ...(presetStyle?.secondaryGrid?.[2] &&
                        presetStyle?.secondaryGrid?.[2]),
                },
            ],
            shouldWrap: true,
        }
    );
}

export function createImgWallMultiple(
    content: string = officialSectionImgWallList[3].content,
    presetStyle: {
        paragraphContent?: string;
        heading?: LayoutNodeStyleMap[string];
        paragraph?: LayoutNodeStyleMap[string];
        img1?: LayoutNodeStyleMap[string];
        img2?: LayoutNodeStyleMap[string];
        img3?: LayoutNodeStyleMap[string];
        img4?: LayoutNodeStyleMap[string];
        img5?: LayoutNodeStyleMap[string];
        img6?: LayoutNodeStyleMap[string];
        primaryGrid?: LayoutNodeStyleMap[string];
        secondaryGrid?: LayoutNodeStyleMap[string][];
    } = {}
) {
    const {
        paragraphContent = i18n.gettext(
            'Problems trying to resolve the conflict between the two major realms of Classical physics: Newtonian mechanics'
        ),
        heading: headingPresetStyle = {},
        paragraph: paragraphPresetStyle = {},
        img1: img1PresetStyle = {},
        img2: img2PresetStyle = {},
        img3: img3PresetStyle = {},
        img4: img4PresetStyle = {},
        img5: img5PresetStyle = {},
        img6: img6PresetStyle = {},
        primaryGrid: primaryGridPresetStyle = {},
        secondaryGrid: secondaryGridPresetStyle = [],
    } = presetStyle;

    const heading = createRichText(OFFICIAL_FONT_SIZE_KEY.HEADING_SMALL, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'center',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(37, 43, 66, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.6',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        ...headingPresetStyle,
    });

    const paragraph = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_MEDIUM,
        paragraphContent,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'center',
            [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '12',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(115, 115, 115, 1)',
            [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.2',
            [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '400',
            [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '24px',
            ...paragraphPresetStyle,
        }
    );

    const img1 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '16px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/img-wall-multiple-1_v1.png',
        ...img1PresetStyle,
    });

    const img2 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '16px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/img-wall-multiple-2_v1.png',
        ...img2PresetStyle,
    });

    const img3 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '16px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/img-wall-multiple-3_v1.png',
        ...img3PresetStyle,
    });

    const img4 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '16px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/img-wall-multiple-4_v1.png',
        ...img4PresetStyle,
    });

    const img5 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '16px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/img-wall-multiple-5_v1.png',
        ...img5PresetStyle,
    });

    const img6 = createMedia(OFFICIAL_MEDIA_KEY.IMAGE, {
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '16px',
        [FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL]:
            '/static/section-showcut/img-wall-multiple-6_v1.png',
        ...img6PresetStyle,
    });

    return combineComponent(
        i18n.gettext('多張圖片牆'),
        [
            [heading, paragraph],
            [[img1], [img2], [img3]],
            [[img4], [img5], [img6]],
        ],
        {
            primaryGridType: GRID_LAYOUT_TYPE.SPLIT_V_3C,
            secondaryGridType: [
                GRID_LAYOUT_TYPE.SINGLE_CELL,
                GRID_LAYOUT_TYPE.SPLIT_H_3C,
                GRID_LAYOUT_TYPE.SPLIT_H_3C,
            ],
            primaryGridPresetStyle: {
                [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '100px',
                [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
                [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
                [FEATURE_FLAG_KEYS.GRID_GAP]: '20px',
                [FEATURE_FLAG_KEYS.PADDING_TOP]: '64px',
                [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '64px',
                [FEATURE_FLAG_KEYS.GRID_ROW_1]: '0.2fr',
                [FEATURE_FLAG_KEYS.GRID_ROW_2]: '0.8fr',
                ...primaryGridPresetStyle,
            },
            secondaryGridPresetStyle: [
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'center',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '10px',
                    ...(secondaryGridPresetStyle?.[0] || {}),
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '20px',
                    ...(secondaryGridPresetStyle?.[1] || {}),
                },
                {
                    [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
                    [FEATURE_FLAG_KEYS.GRID_GAP]: '20px',
                    ...(secondaryGridPresetStyle?.[2] || {}),
                },
            ],
            shouldWrap: true,
        }
    );
}

export const officialSectionImgWallGenMap = {
    [OFFICIAL_SECTION_IMG_WALL_KEY.TWO]: createImgWallTwo,
    [OFFICIAL_SECTION_IMG_WALL_KEY.THREE]: createImgWallThree,
    [OFFICIAL_SECTION_IMG_WALL_KEY.FOUR]: createImgWallFour,
    [OFFICIAL_SECTION_IMG_WALL_KEY.MULTIPLE]: createImgWallMultiple,
};
